import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Send, ChevronRight, ChevronLeft, Building2, Target, BarChart3, Globe, CheckCircle2, AlertCircle } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ['company', 'market', 'needs', 'contact'];

export const LeadQualificationForm = () => {
  const { language } = useLanguage();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    company_name: '',
    sector: '',
    revenue_range: '',
    employees: '',
    market_france: false,
    market_europe: false,
    market_africa: false,
    market_other: false,
    service_audit: false,
    service_conseil: false,
    service_management: false,
    main_challenge: '',
    timeline: '',
    budget_range: '',
    name: '',
    email: '',
    phone: '',
    role: '',
    how_found: '',
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const t = {
    fr: {
      title: 'Qualifiez votre projet',
      subtitle: 'Répondez en 2 minutes pour recevoir une analyse personnalisée',
      steps: ['Entreprise', 'Marchés', 'Besoins', 'Contact'],
      // Step 1
      companyName: 'Raison sociale',
      sector: 'Secteur d\'activité',
      sectorOptions: ['Services', 'Industrie', 'Commerce', 'Tech / Digital', 'Santé', 'Finance / Banque', 'Immobilier', 'Énergie', 'Agroalimentaire', 'Transport / Logistique', 'Autre'],
      revenue: 'Chiffre d\'affaires annuel',
      revenueOptions: ['< 50 000 €', '50 000 € - 200 000 €', '200 000 € - 500 000 €', '500 000 € - 1 M€', '1 M€ - 5 M€', '5 M€ - 20 M€', '> 20 M€'],
      employees: 'Nombre de collaborateurs',
      employeesOptions: ['1-5', '6-20', '21-50', '51-200', '200+'],
      // Step 2
      marketTitle: 'Vos marchés cibles',
      marketFrance: 'France',
      marketEurope: 'Europe',
      marketAfrica: 'Afrique',
      marketOther: 'Autres marchés internationaux',
      // Step 3
      serviceTitle: 'Services recherchés',
      serviceAudit: 'Audit stratégique',
      serviceConseil: 'Conseil en stratégie & performance',
      serviceManagement: 'Management de transition',
      challenge: 'Quelle est votre problématique principale ?',
      challengePlaceholder: 'Ex: Nous souhaitons restructurer notre stratégie commerciale pour accélérer notre croissance à l\'international...',
      timeline: 'Quel est votre délai souhaité ?',
      timelineOptions: ['Urgent (< 1 mois)', 'Court terme (1-3 mois)', 'Moyen terme (3-6 mois)', 'Pas de contrainte de délai'],
      budget: 'Budget envisagé',
      budgetOptions: ['< 5 000 €', '5 000 € - 10 000 €', '10 000 € - 25 000 €', '25 000 € - 50 000 €', '> 50 000 €', 'À définir ensemble'],
      // Step 4
      name: 'Nom complet',
      email: 'Email professionnel',
      phone: 'Téléphone',
      role: 'Votre fonction',
      roleOptions: ['Dirigeant / CEO', 'Directeur Général', 'Directeur Financier (DAF)', 'Directeur Commercial', 'Directeur des Opérations', 'Responsable Stratégie', 'Autre'],
      howFound: 'Comment nous avez-vous connu ?',
      howOptions: ['Google', 'LinkedIn', 'Recommandation', 'Réseaux sociaux', 'Événement / Salon', 'Autre'],
      // Navigation
      next: 'Suivant',
      prev: 'Précédent',
      submit: 'Envoyer ma demande',
      success: 'Demande envoyée !',
      successMsg: 'Notre équipe analysera votre profil et vous contactera sous 24h avec une proposition personnalisée.',
      newRequest: 'Nouvelle demande',
      required: 'Champs obligatoires',
    },
    en: {
      title: 'Qualify your project',
      subtitle: 'Answer in 2 minutes to receive a personalized analysis',
      steps: ['Company', 'Markets', 'Needs', 'Contact'],
      companyName: 'Company name',
      sector: 'Industry',
      sectorOptions: ['Services', 'Manufacturing', 'Retail', 'Tech / Digital', 'Healthcare', 'Finance / Banking', 'Real Estate', 'Energy', 'Agri-food', 'Transport / Logistics', 'Other'],
      revenue: 'Annual revenue',
      revenueOptions: ['< €50K', '€50K - €200K', '€200K - €500K', '€500K - €1M', '€1M - €5M', '€5M - €20M', '> €20M'],
      employees: 'Number of employees',
      employeesOptions: ['1-5', '6-20', '21-50', '51-200', '200+'],
      marketTitle: 'Your target markets',
      marketFrance: 'France',
      marketEurope: 'Europe',
      marketAfrica: 'Africa',
      marketOther: 'Other international markets',
      serviceTitle: 'Services needed',
      serviceAudit: 'Strategic Audit',
      serviceConseil: 'Strategy & Performance Advisory',
      serviceManagement: 'Transition Management',
      challenge: 'What is your main challenge?',
      challengePlaceholder: 'E.g: We want to restructure our business strategy to accelerate international growth...',
      timeline: 'Desired timeline?',
      timelineOptions: ['Urgent (< 1 month)', 'Short term (1-3 months)', 'Medium term (3-6 months)', 'No time constraint'],
      budget: 'Estimated budget',
      budgetOptions: ['< €5K', '€5K - €10K', '€10K - €25K', '€25K - €50K', '> €50K', 'To be discussed'],
      name: 'Full name',
      email: 'Professional email',
      phone: 'Phone',
      role: 'Your position',
      roleOptions: ['CEO / Founder', 'General Manager', 'CFO', 'Sales Director', 'COO', 'Strategy Manager', 'Other'],
      howFound: 'How did you find us?',
      howOptions: ['Google', 'LinkedIn', 'Referral', 'Social media', 'Event / Conference', 'Other'],
      next: 'Next',
      prev: 'Previous',
      submit: 'Submit my request',
      success: 'Request sent!',
      successMsg: 'Our team will analyze your profile and contact you within 24h with a personalized proposal.',
      newRequest: 'New request',
      required: 'Required fields',
    }
  }[language];

  const handleSubmit = async () => {
    if (!form.name || !form.email) {
      toast.error(t.required);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
      toast.success(t.success);
    } catch {
      toast.error('Error');
    } finally {
      setLoading(false);
    }
  };

  const RadioGroup = ({ options, value, onChange }) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {options.map(opt => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={`text-left px-3 py-2.5 rounded-lg text-sm border transition-all ${
            value === opt
              ? 'border-[#4caf50] bg-[#4caf50]/10 text-[#004d40] font-medium'
              : 'border-slate-200 hover:border-[#4caf50]/50 text-slate-600'
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );

  const CheckOption = ({ label, checked, onChange }) => (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-sm transition-all w-full text-left ${
        checked
          ? 'border-[#4caf50] bg-[#4caf50]/10 text-[#004d40] font-medium'
          : 'border-slate-200 hover:border-[#4caf50]/50 text-slate-600'
      }`}
    >
      <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${checked ? 'bg-[#4caf50]' : 'border-2 border-slate-300'}`}>
        {checked && <CheckCircle2 className="w-4 h-4 text-white" />}
      </div>
      {label}
    </button>
  );

  if (submitted) {
    return (
      <div className="text-center py-12" data-testid="lead-form-success">
        <div className="w-20 h-20 mx-auto rounded-full bg-[#4caf50]/10 flex items-center justify-center mb-6">
          <CheckCircle2 className="w-10 h-10 text-[#4caf50]" />
        </div>
        <h3 className="text-2xl font-bold text-[#004d40] mb-3">{t.success}</h3>
        <p className="text-slate-600 max-w-md mx-auto mb-6">{t.successMsg}</p>
        <button onClick={() => { setSubmitted(false); setStep(0); setForm({ company_name: '', sector: '', revenue_range: '', employees: '', market_france: false, market_europe: false, market_africa: false, market_other: false, service_audit: false, service_conseil: false, service_management: false, main_challenge: '', timeline: '', budget_range: '', name: '', email: '', phone: '', role: '', how_found: '' }); }}
          className="text-[#004d40] font-medium text-sm hover:text-[#4caf50] transition-colors"
          data-testid="lead-form-reset"
        >
          {t.newRequest}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="lead-qualification-form">
      {/* Progress */}
      <div className="flex items-center justify-between mb-8">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center flex-1">
            <div className={`flex items-center gap-2 ${i <= step ? 'text-[#004d40]' : 'text-slate-300'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                i < step ? 'bg-[#4caf50] text-white' : i === step ? 'bg-[#004d40] text-white' : 'bg-slate-100 text-slate-400'
              }`}>
                {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </div>
              <span className="text-xs font-medium hidden sm:block">{t.steps[i]}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${i < step ? 'bg-[#4caf50]' : 'bg-slate-200'}`} />}
          </div>
        ))}
      </div>

      {/* Step 1: Company */}
      {step === 0 && (
        <div className="space-y-5" data-testid="lead-step-company">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t.companyName} *</label>
            <Input value={form.company_name} onChange={e => update('company_name', e.target.value)} className="form-input" data-testid="lead-company-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t.sector} *</label>
            <RadioGroup options={t.sectorOptions} value={form.sector} onChange={v => update('sector', v)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t.revenue} *</label>
            <RadioGroup options={t.revenueOptions} value={form.revenue_range} onChange={v => update('revenue_range', v)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t.employees}</label>
            <RadioGroup options={t.employeesOptions} value={form.employees} onChange={v => update('employees', v)} />
          </div>
        </div>
      )}

      {/* Step 2: Markets */}
      {step === 1 && (
        <div className="space-y-5" data-testid="lead-step-market">
          <h3 className="font-bold text-[#004d40] flex items-center gap-2"><Globe className="w-5 h-5" />{t.marketTitle}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <CheckOption label={t.marketFrance} checked={form.market_france} onChange={v => update('market_france', v)} />
            <CheckOption label={t.marketEurope} checked={form.market_europe} onChange={v => update('market_europe', v)} />
            <CheckOption label={t.marketAfrica} checked={form.market_africa} onChange={v => update('market_africa', v)} />
            <CheckOption label={t.marketOther} checked={form.market_other} onChange={v => update('market_other', v)} />
          </div>
        </div>
      )}

      {/* Step 3: Needs */}
      {step === 2 && (
        <div className="space-y-5" data-testid="lead-step-needs">
          <div>
            <h3 className="font-bold text-[#004d40] flex items-center gap-2 mb-3"><Target className="w-5 h-5" />{t.serviceTitle}</h3>
            <div className="space-y-2">
              <CheckOption label={t.serviceAudit} checked={form.service_audit} onChange={v => update('service_audit', v)} />
              <CheckOption label={t.serviceConseil} checked={form.service_conseil} onChange={v => update('service_conseil', v)} />
              <CheckOption label={t.serviceManagement} checked={form.service_management} onChange={v => update('service_management', v)} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t.challenge} *</label>
            <Textarea value={form.main_challenge} onChange={e => update('main_challenge', e.target.value)} rows={3} className="form-input resize-none" placeholder={t.challengePlaceholder} data-testid="lead-challenge" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t.timeline}</label>
            <RadioGroup options={t.timelineOptions} value={form.timeline} onChange={v => update('timeline', v)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t.budget}</label>
            <RadioGroup options={t.budgetOptions} value={form.budget_range} onChange={v => update('budget_range', v)} />
          </div>
        </div>
      )}

      {/* Step 4: Contact */}
      {step === 3 && (
        <div className="space-y-5" data-testid="lead-step-contact">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t.name} *</label>
              <Input value={form.name} onChange={e => update('name', e.target.value)} className="form-input" data-testid="lead-name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t.email} *</label>
              <Input type="email" value={form.email} onChange={e => update('email', e.target.value)} className="form-input" data-testid="lead-email" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t.phone}</label>
              <Input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} className="form-input" data-testid="lead-phone" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t.role}</label>
              <RadioGroup options={t.roleOptions} value={form.role} onChange={v => update('role', v)} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t.howFound}</label>
            <RadioGroup options={t.howOptions} value={form.how_found} onChange={v => update('how_found', v)} />
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
        {step > 0 ? (
          <button onClick={() => setStep(s => s - 1)} className="inline-flex items-center gap-2 text-slate-500 hover:text-[#004d40] font-medium text-sm transition-colors" data-testid="lead-prev">
            <ChevronLeft className="w-4 h-4" /> {t.prev}
          </button>
        ) : <div />}
        {step < STEPS.length - 1 ? (
          <button onClick={() => setStep(s => s + 1)} className="inline-flex items-center gap-2 bg-[#004d40] hover:bg-[#00695c] text-white px-6 py-3 rounded-full font-medium text-sm transition-colors" data-testid="lead-next">
            {t.next} <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={handleSubmit} disabled={loading} className="inline-flex items-center gap-2 bg-[#4caf50] hover:bg-[#388e3c] text-white px-8 py-3 rounded-full font-bold text-sm transition-colors" data-testid="lead-submit">
            {loading ? <div className="spinner" /> : <><Send className="w-4 h-4" /> {t.submit}</>}
          </button>
        )}
      </div>
    </div>
  );
};
